import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import getTenantIdFromURL from "./utils/getTenantIdFromURL";
import { getPaymentPlatformEntity } from "./api/companyApi";

const C = {
  ink: "#0b2a4a",
  blue: "#1e6fe8",
  muted: "#64748b",
  line: "#e3ebf3",
  green: "#12a150",
  amber: "#d97706",
  red: "#dc2626",
  bg: "#f6f9fc",
};
const money = (value) =>
  value == null
    ? "—"
    : `$${Number(value).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const valueText = (value) => {
  if (value == null || value === "") return "—";
  if (typeof value === "number")
    return value.toLocaleString(undefined, { maximumFractionDigits: 3 });
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) return value.length ? value.join(", ") : "—";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};
const label = (key) =>
  String(key || "")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replaceAll("_", " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
const color = (decision) =>
  decision === "approve"
    ? C.green
    : decision === "review"
      ? C.amber
      : decision === "decline"
        ? C.red
        : C.muted;

function Metric({ title, value, detail }) {
  return (
    <Card
      variant="outlined"
      sx={{ p: 2, borderRadius: 3, borderColor: C.line }}
    >
      <Typography
        sx={{
          color: C.muted,
          fontSize: 11,
          fontWeight: 900,
          textTransform: "uppercase",
        }}
      >
        {title}
      </Typography>
      <Typography
        sx={{ color: C.ink, fontSize: 24, lineHeight: 1.25, fontWeight: 950 }}
      >
        {value}
      </Typography>
      {detail && (
        <Typography sx={{ color: C.muted, fontSize: 12 }}>{detail}</Typography>
      )}
    </Card>
  );
}

function FieldGrid({ data, empty = "No data was supplied." }) {
  const entries = Object.entries(data || {}).filter(
    ([, value]) => value !== null && value !== undefined && value !== "",
  );
  if (!entries.length)
    return <Typography sx={{ color: C.muted }}>{empty}</Typography>;
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "repeat(3, 1fr)" },
        gap: 1,
      }}
    >
      {entries.map(([key, value]) => (
        <Box
          key={key}
          sx={{
            p: 1.35,
            border: `1px solid ${C.line}`,
            borderRadius: 2,
            minWidth: 0,
          }}
        >
          <Typography
            sx={{
              color: C.muted,
              fontSize: 10.5,
              fontWeight: 850,
              textTransform: "uppercase",
            }}
          >
            {label(key)}
          </Typography>
          <Typography
            title={valueText(value)}
            sx={{
              color: C.ink,
              fontSize: 13.5,
              fontWeight: 650,
              overflowWrap: "anywhere",
            }}
          >
            {valueText(value)}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <Paper
      variant="outlined"
      sx={{ p: 2.25, mb: 2, borderColor: C.line, borderRadius: 3 }}
    >
      <Typography sx={{ color: C.ink, fontSize: 17, fontWeight: 950 }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography sx={{ color: C.muted, fontSize: 12.5, mb: 1.75 }}>
          {subtitle}
        </Typography>
      )}
      {!subtitle && <Box sx={{ mb: 1.5 }} />}
      {children}
    </Paper>
  );
}

export default function PaymentPlatformCompanyReport({ adminAction = false }) {
  const { entityId } = useParams();
  const navigate = useNavigate();
  const isAdmin = useSelector((state) => state.userInfo.isAdmin);
  const currentTenantId = useSelector((state) => state.userInfo.tenantId);
  const tenantId =
    isAdmin || adminAction ? getTenantIdFromURL() : currentTenantId;
  const [entity, setEntity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tab, setTab] = useState(0);

  useEffect(() => {
    let active = true;
    if (!tenantId || !entityId) return undefined;
    setLoading(true);
    setError("");
    getPaymentPlatformEntity(tenantId, entityId)
      .then((data) => {
        if (active) setEntity(data);
      })
      .catch((requestError) => {
        if (active)
          setError(
            requestError?.response?.data?.message ||
              "Could not load the company report",
          );
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [tenantId, entityId]);

  const assessment = entity?.assessment || {};
  const recommendation = assessment.recommendation || {};
  const backPath =
    isAdmin || adminAction
      ? `/tenant-dashboard/${tenantId}/assessments`
      : "/assessments";

  if (loading)
    return (
      <Box sx={{ p: 8, textAlign: "center" }}>
        <CircularProgress />
      </Box>
    );
  if (error || !entity)
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error || "Company report not found"}</Alert>
      </Box>
    );
  const displayName =
    entity.identity?.companyName ||
    entity.identity?.externalId ||
    entity.identity?.website ||
    "Unidentified merchant";

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1500, mx: "auto" }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(backPath)}
        sx={{ mb: 1, textTransform: "none" }}
      >
        All companies
      </Button>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 2,
          flexWrap: "wrap",
          mb: 2,
        }}
      >
        <Box>
          <Typography sx={{ color: C.ink, fontSize: 29, fontWeight: 950 }}>
            {displayName}
          </Typography>
          <Typography sx={{ color: C.muted }}>
            {entity.identity?.externalId ||
              entity.identity?.registrationNumber ||
              entity.id}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Chip
            label={`Match ${Math.round((entity.match?.confidence || 0) * 100)}%`}
            variant="outlined"
          />
          <Chip
            label={`${assessment.coverage || 0}% evidence`}
            variant="outlined"
          />
          <Chip
            label={(assessment.decision || "unscored").replaceAll("_", " ")}
            sx={{
              color: color(assessment.decision),
              bgcolor: `${color(assessment.decision)}12`,
              fontWeight: 900,
              textTransform: "capitalize",
            }}
          />
        </Box>
      </Box>
      {assessment.validation?.status !== "ready_for_backtest" && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>Draft policy outcome:</strong> this recommendation is not a
          validated prediction. {assessment.validation?.reason}
        </Alert>
      )}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(6, 1fr)" },
          gap: 1.25,
          mb: 2,
        }}
      >
        <Metric
          title="Score"
          value={assessment.score ?? "—"}
          detail={`Model v${assessment.modelVersion || 1}`}
        />
        <Metric
          title="Decision"
          value={label(assessment.decision || "unscored")}
        />
        <Metric
          title="Requested"
          value={money(entity.signals?.requestedAmount)}
        />
        <Metric title="Recommended" value={money(recommendation.amount)} />
        <Metric
          title="Term"
          value={
            recommendation.termMonths ? `${recommendation.termMonths} mo` : "—"
          }
          detail={
            recommendation.factorRate
              ? `Factor ${recommendation.factorRate}`
              : null
          }
        />
        <Metric
          title="Applications"
          value={(
            entity.applicationCount ||
            entity.applications?.length ||
            0
          ).toLocaleString()}
        />
      </Box>

      <Paper
        variant="outlined"
        sx={{ borderColor: C.line, borderRadius: 3, overflow: "hidden" }}
      >
        <Tabs
          value={tab}
          onChange={(event, value) => setTab(value)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ px: 1.5 }}
        >
          <Tab label="Decision & offer" />
          <Tab label="Identity & verification" />
          <Tab label="Applications & outcomes" />
          <Tab label="Platform & financial data" />
        </Tabs>
        <Divider />
        <Box sx={{ p: { xs: 1.5, md: 2.5 }, bgcolor: C.bg }}>
          {tab === 0 && (
            <>
              <Section
                title="Complete recommendation"
                subtitle="Eligibility, risk result and commercial offer generated from the evidence available for this merchant."
              >
                <FieldGrid
                  data={{
                    decision: assessment.decision,
                    score: assessment.score,
                    evidenceCoverage: `${assessment.coverage || 0}%`,
                    riskBand: recommendation.riskBand,
                    product: recommendation.product,
                    requestedAmount: entity.signals?.requestedAmount,
                    recommendedAmount: recommendation.amount,
                    termMonths: recommendation.termMonths,
                    factorRate: recommendation.factorRate,
                    maxPaymentShare: recommendation.maxPaymentShare,
                    requiresManualReview: recommendation.requiresManualReview,
                    modelVersion: assessment.modelVersion,
                  }}
                />
              </Section>
              <Section
                title="Score drivers"
                subtitle="Every feature used in the score, including its normalized value and configured weight."
              >
                {(assessment.evidence || []).length ? (
                  <Box
                    component="table"
                    sx={{
                      width: "100%",
                      borderCollapse: "collapse",
                      "th,td": {
                        p: 1.2,
                        borderBottom: `1px solid ${C.line}`,
                        textAlign: "left",
                        fontSize: 13,
                      },
                      th: {
                        color: C.muted,
                        fontSize: 11,
                        textTransform: "uppercase",
                      },
                    }}
                  >
                    <thead>
                      <tr>
                        <th>Evidence</th>
                        <th>Normalized value</th>
                        <th>Weight</th>
                        <th>Weighted contribution</th>
                      </tr>
                    </thead>
                    <tbody>
                      {assessment.evidence.map((item) => (
                        <tr key={item.feature}>
                          <td>
                            <strong>{label(item.feature)}</strong>
                          </td>
                          <td>{valueText(item.value)}</td>
                          <td>{item.weight}</td>
                          <td>{Number(item.value * item.weight).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Box>
                ) : (
                  <Typography sx={{ color: C.muted }}>
                    No score evidence was available.
                  </Typography>
                )}
              </Section>
              <Section title="Missing evidence">
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  {(assessment.missingFeatures || []).map((feature) => (
                    <Chip
                      key={feature}
                      size="small"
                      label={label(feature)}
                      variant="outlined"
                    />
                  ))}
                  {!assessment.missingFeatures?.length && (
                    <Typography sx={{ color: C.muted }}>
                      No configured evidence is missing.
                    </Typography>
                  )}
                </Box>
              </Section>
            </>
          )}
          {tab === 1 && (
            <>
              <Section
                title="Verified identity information"
                subtitle="Business identity details available for this assessment."
              >
                <FieldGrid data={entity.identity} />
              </Section>
              <Section title="Verification summary">
                <FieldGrid
                  data={{
                    matchConfidence: `${Math.round((entity.match?.confidence || 0) * 100)}%`,
                    evidenceCoverage: `${assessment.coverage || 0}%`,
                    verificationStatus:
                      (entity.match?.confidence || 0) >= 0.8
                        ? "Verified"
                        : "Review required",
                  }}
                />
              </Section>
            </>
          )}
          {tab === 2 && (
            <>
              <Section title="Application identifiers">
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  {(entity.applications || []).map((application) => (
                    <Chip
                      key={application}
                      label={application}
                      variant="outlined"
                    />
                  ))}
                  {!entity.applications?.length && (
                    <Typography sx={{ color: C.muted }}>
                      No application identifiers supplied.
                    </Typography>
                  )}
                </Box>
              </Section>
              <Section title="Complete historical outcomes">
                <FieldGrid
                  data={{
                    fundedCount: entity.outcomes?.funded,
                    notFundedCount: entity.outcomes?.notFunded,
                    ...(entity.outcomes?.decisions || {}),
                  }}
                />
              </Section>
            </>
          )}
          {tab === 3 && (
            <>
              <Section title="Normalized platform and financial signals">
                <FieldGrid data={entity.signals} />
              </Section>
              <Section title="Signal history and ranges">
                <FieldGrid data={entity.signalStats} />
              </Section>
            </>
          )}
        </Box>
      </Paper>
    </Box>
  );
}
